'use client';

import { useEffect, useMemo } from 'react';
import {
  MobileModalDebugDomProbePhase,
  MobileModalDebugEvent,
  MobileModalDebugShell,
  captureMobileModalDomSnapshot,
} from './mobile-modal-debug';
import type { PersistentDebugLogger } from './use-persistent-debug-logger';

interface UseMobileModalDomProbeInput {
  debugName?: string;
  isTop: boolean;
  logger: PersistentDebugLogger;
  modalId: string;
  shell: MobileModalDebugShell;
  state: Record<string, unknown>;
  visible: boolean;
}

function getLifecycleEventName(shell: MobileModalDebugShell): MobileModalDebugEvent {
  return shell === MobileModalDebugShell.Split
    ? MobileModalDebugEvent.SplitModalDomProbeLifecycle
    : MobileModalDebugEvent.StretchModalDomProbeLifecycle;
}

export function useMobileModalDomProbe({
  debugName,
  isTop,
  logger,
  modalId,
  shell,
  state,
  visible,
}: UseMobileModalDomProbeInput): void {
  const lifecycleEventName = getLifecycleEventName(shell);
  const serializedState = useMemo(() => JSON.stringify(state), [state]);

  useEffect(() => {
    if (!debugName) {
      return undefined;
    }

    logger.logMarker({
      name: lifecycleEventName,
      payload: {
        debugName,
        isTop,
        modalId,
        phase: MobileModalDebugDomProbePhase.Effect,
        shell,
        snapshot: captureMobileModalDomSnapshot({ modalId }),
        state,
        visible,
      },
    });

    return () => {
      logger.logMarker({
        name: lifecycleEventName,
        payload: {
          debugName,
          isTop,
          modalId,
          phase: MobileModalDebugDomProbePhase.Cleanup,
          shell,
          snapshot: captureMobileModalDomSnapshot({ modalId }),
          state,
          visible,
        },
      });
    };
  }, [
    debugName,
    isTop,
    lifecycleEventName,
    logger,
    modalId,
    serializedState,
    shell,
    state,
    visible,
  ]);
}
